'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveLoteProcessing } from '@/features/negociacoes/hooks/useActiveLoteProcessing';
import { useNegociacaoLoteStatusEvents } from '@/features/negociacoes/hooks/useNegociacaoLoteStatusEvents';
import type { NegociacaoLoteStatus, NegociacaoLoteTotais } from '@/features/negociacoes/types/loteStatus.types';
import { fetchLoteStatus } from '@/features/negociacoes/store/actions';
import { useAppDispatch } from '@/store/hooks';
import { FeatherLoader } from '@subframe/core';
import { useCallback, useEffect, useState } from 'react';
const TERMINAL_STATUSES: NegociacaoLoteStatus[] = ['COMPLETED', 'FAILED', 'CANCELED'];
const DEFAULT_TOTALS: NegociacaoLoteTotais = {
  total: 0,
  pending: 0,
  valid: 0,
  processing: 0,
  existing: 0,
  notFound: 0,
  missingPhone: 0,
  missingValue: 0,
  conflict: 0,
  error: 0
};
interface LoteProgressBannerProps {
  onComplete?: () => void;
}
export function LoteProgressBanner(t0) {
  const $ = _c(35);
  const {
    onComplete
  } = t0;
  const dispatch = useAppDispatch();
  const {
    activeLoteId,
    clearActiveLoteId
  } = useActiveLoteProcessing();
  const [totals, setTotals] = useState(DEFAULT_TOTALS);
  const [status, setStatus] = useState(null);
  let t1;
  let t2;
  if ($[0] !== activeLoteId || $[1] !== clearActiveLoteId || $[2] !== dispatch || $[3] !== onComplete) {
    t1 = () => {
      if (!activeLoteId) {
        setTotals(DEFAULT_TOTALS);
        setStatus(null);
        return;
      }
      let canceled = false;
      dispatch(fetchLoteStatus({
        loteId: activeLoteId
      })).unwrap().then(lote => {
        if (canceled) {
          return;
        }
        setTotals(lote.totais ?? DEFAULT_TOTALS);
        setStatus(lote.status);
        if (TERMINAL_STATUSES.includes(lote.status)) {
          clearActiveLoteId();
          onComplete?.();
        }
      }).catch(() => {
        if (canceled) {
          return;
        }
        clearActiveLoteId();
      });
      return () => {
        canceled = true;
      };
    };
    t2 = [activeLoteId, dispatch, clearActiveLoteId, onComplete];
    $[0] = activeLoteId;
    $[1] = clearActiveLoteId;
    $[2] = dispatch;
    $[3] = onComplete;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      if (event.totais) {
        setTotals(event.totais);
      }
      setStatus(event.status);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleStatusChange = t3;
  let t4;
  if ($[7] !== clearActiveLoteId || $[8] !== onComplete) {
    t4 = () => {
      clearActiveLoteId();
      onComplete?.();
    };
    $[7] = clearActiveLoteId;
    $[8] = onComplete;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleComplete = t4;
  const t5 = !!activeLoteId;
  let t6;
  if ($[10] !== handleComplete || $[11] !== t5) {
    t6 = {
      enabled: t5,
      onStatusChange: handleStatusChange,
      onComplete: handleComplete
    };
    $[10] = handleComplete;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useNegociacaoLoteStatusEvents(activeLoteId, t6);
  if (!activeLoteId) {
    return null;
  }
  const total = totals.total;
  const pending = totals.pending;
  const processed = Math.max(0, total - pending);
  let t7;
  if ($[13] !== processed || $[14] !== total) {
    t7 = total > 0 ? Math.min(100, Math.round(processed / total * 100)) : 0;
    $[13] = processed;
    $[14] = total;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const percent = t7;
  const title = status === "FAILED" ? "Falha ao processar o lote" : status === "CANCELED" ? "Processamento do lote cancelado" : "Processando as negocia\xE7\xF5es do seu lote";
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FeatherLoader className="text-brand-600 h-5 w-5 flex-none animate-spin" />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== title) {
    t9 = <div className="flex min-w-0 items-center gap-3">{t8}<span className="text-body-bold font-body-bold text-default-font truncate">{title}</span></div>;
    $[17] = title;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== processed || $[20] !== total) {
    t10 = <span className="text-caption font-caption text-subtext-color">{processed} de {total}</span>;
    $[19] = processed;
    $[20] = total;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== percent) {
    t11 = <span className="text-body-bold font-body-bold text-default-font">{percent}%</span>;
    $[22] = percent;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <div className="flex flex-none items-center gap-3">{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t9) {
    t13 = <div className="flex w-full items-center justify-between gap-4">{t9}{t12}</div>;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = `${percent}%`;
  let t15;
  if ($[30] !== t14) {
    t15 = <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-100"><div className="bg-brand-600 h-full rounded-full transition-all duration-500 ease-out" style={{
        width: t14
      }} /></div>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15) {
    t16 = <div className="flex w-full flex-col gap-3 rounded-[16px] border border-solid border-neutral-200 bg-white px-5 py-4 shadow-sm">{t13}{t15}</div>;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}